import { useRef, useState, type DragEvent } from 'react';

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

/**
 * Drop-zone handlers for a container. `dragging` is true while a file is held over it (enter/leave events of child
 * elements are counted so it does not flicker). Spread `handlers` on the container.
 */
export function useFileDrop(onFiles: (files: FileList) => void, enabled = true) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const reset = () => {
    depth.current = 0;
    setDragging(false);
  };
  const handlers = {
    onDragEnter(e: DragEvent) {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      depth.current++;
      setDragging(true);
    },
    onDragOver(e: DragEvent) {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault(); // required for the drop to fire
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave(e: DragEvent) {
      if (!enabled || !hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setDragging(false);
    },
    onDrop(e: DragEvent) {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      reset();
      if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
    },
  };
  return { dragging, handlers };
}
